import { Eye, Pencil, Trash2, User } from "lucide-react";
import StatusPill from "shared/StatusPill";
import LoggedOn from "shared/LoggedOn";
import RowActionsMenu from "shared/RowActionsMenu";
import type { RowAction } from "shared/RowActionsMenu";
import type { Meeting } from "../api/MeetingsApi";
import { cn } from "lib/utils";
import Highlight from "./Highlight";
import { CONDUCTOR_TONE, formatMeetingDate } from "./meetingUtils";

interface MeetingRowProps {
  meeting: Meeting;
  showEmployee: boolean;
  /** Current notes/employee search, highlighted in the row. */
  highlight?: string;
  canEdit: boolean;
  canDelete: boolean;
  onOpen: (m: Meeting) => void;
  onEdit: (m: Meeting) => void;
  onDelete: (id: number) => void;
}

/** One meeting in the timeline. The whole card opens the read-only detail. */
export default function MeetingRow({ meeting, showEmployee, highlight, canEdit, canDelete, onOpen, onEdit, onDelete }: MeetingRowProps) {
  // Issue and outcome both show; follow-up only stands in when neither was written.
  const previews = [
    { label: "Issue", text: meeting.before_meeting_notes, accent: "border-warning-border" },
    { label: "Outcome", text: meeting.after_meeting_remarks, accent: "border-success-border" },
  ].filter((p) => p.text);
  if (previews.length === 0 && meeting.follow_up) previews.push({ label: "Follow-up", text: meeting.follow_up, accent: "border-info-border" });

  const actions: RowAction[] = [{ label: "View details", icon: Eye, onClick: () => onOpen(meeting) }];
  if (canEdit) actions.push({ label: "Edit", icon: Pencil, onClick: () => onEdit(meeting) });
  if (canDelete) actions.push({ label: "Delete", icon: Trash2, destructive: true, onClick: () => onDelete(meeting.id) });

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(meeting)}
      onKeyDown={(e) => { if (e.target !== e.currentTarget) return; if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(meeting); } }}
      className="bg-surface border border-border rounded-xl p-4 cursor-pointer hover:border-primary/50 focus:outline-none focus:ring-1 focus:ring-ring transition-colors"
    >
      <div className="flex items-start gap-4">
        <div className="shrink-0 w-14 text-center rounded-lg bg-surface-muted border border-border py-1.5">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground leading-none">
            {new Date(meeting.meeting_date.slice(0, 10) + "T00:00:00").toLocaleDateString(undefined, { month: "short" })}
          </p>
          <p className="text-lg font-semibold text-foreground leading-tight">{Number(meeting.meeting_date.slice(8, 10))}</p>
          <p className="text-[10px] text-muted-foreground leading-none">{meeting.meeting_date.slice(0, 4)}</p>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {showEmployee && (
              <span className="text-sm font-semibold text-foreground">
                <Highlight text={meeting.employee_name} term={highlight} />
                {meeting.designation && <span className="font-normal text-muted-foreground"> · {meeting.designation}</span>}
              </span>
            )}
            <StatusPill tone={CONDUCTOR_TONE[meeting.conductor_role]}>{meeting.conductor_role}</StatusPill>
            {meeting.conductor_employee_name && (
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                <User className="w-3 h-3" />
                {meeting.conductor_employee_name}
              </span>
            )}
            {!showEmployee && (
              <span className="text-xs text-muted-foreground">{formatMeetingDate(meeting.meeting_date)}</span>
            )}
          </div>

          {previews.length > 0 ? (
            previews.map((p) => (
              <p key={p.label} className={cn("mt-2 pl-3 border-l-2 text-sm text-foreground/80 line-clamp-2 whitespace-pre-line", p.accent)}>
                <span className="text-xs font-medium text-muted-foreground mr-1.5">{p.label}:</span>
                <Highlight text={p.text!} term={highlight} />
              </p>
            ))
          ) : (
            <p className="mt-1.5 text-sm text-muted-foreground italic">No notes recorded.</p>
          )}
          <LoggedOn recordDate={meeting.meeting_date} recordedAt={meeting.created_at} className="mt-2" />
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <RowActionsMenu actions={actions} />
        </div>
      </div>
    </div>
  );
}
